import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SectionLabel } from "@/components/yanre/SectionLabel";
import { Reveal, RevealLines } from "@/components/yanre/Reveal";
import { useLocale } from "@/i18n/LocaleContext";

// Premium editorial equipment showcase — the machine is the hero.
// items: { category, series?, statement?, image } — media swappable without redesign.
export const FeaturedEquipment = ({ items, index }) => {
  const { t } = useLocale();
  const [active, setActive] = useState(0);
  const item = items[active];
  return (
    <section id="equipment" className="section-dark relative overflow-hidden border-t border-carbon-700" data-testid="featured-equipment-section">
      <div className="container-edge relative py-16 lg:py-24">
        <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-end">
          <div>
            <Reveal>
              <SectionLabel index={index}>Featured Equipment</SectionLabel>
            </Reveal>
            <h2 className="mt-8 font-display text-4xl font-extrabold uppercase leading-[0.92] tracking-tight text-white sm:text-5xl lg:text-6xl">
              <RevealLines
                lines={[
                  t("fe.h1a", "Designed to perform."),
                  <span key="b">
                    {t("fe.h1b", "Built to be seen.")}
                  </span>,
                ]}
              />
            </h2>
          </div>
          <Reveal delay={0.15}>
            <p className="max-w-md text-lg font-light leading-relaxed text-titanium/70 lg:justify-self-end">
              {t("fe.tagline", "Your equipment is part of your brand.")}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-2" data-testid="equipment-tabs">
            {items.map((it, i) => (
              <button
                key={it.category}
                type="button"
                onClick={() => setActive(i)}
                className={`border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 sm:text-[11px] ${
                  i === active
                    ? "border-signal bg-signal text-white"
                    : "border-carbon-600 text-titanium/60 hover:border-titanium/50 hover:text-titanium"
                }`}
                data-testid={`equipment-tab-${i}`}
              >
                {t(`fe.i${i}.cat`, it.category)}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative mt-6 overflow-hidden border border-carbon-700 bg-carbon-800" data-testid="equipment-stage">
            <div className="relative aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/10]">
              <AnimatePresence mode="wait">
                <motion.img
                  key={item.category}
                  src={item.image}
                  srcSet={item.srcSet}
                  sizes={item.srcSet ? "(max-width: 640px) 100vw, 1400px" : undefined}
                  alt={item.alt || item.category}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-carbon-950/90 via-transparent to-carbon-950/20" />
              <span className="absolute right-5 top-5 font-mono text-[11px] uppercase tracking-[0.25em] text-titanium/70 rtl:left-5 rtl:right-auto" data-testid="equipment-counter">
                {String(active + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </span>
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-signal sm:text-[11px]">
                  {t(`fe.i${active}.cat`, item.category)}
                </p>
                {item.series && (
                  <h3 className="mt-2 font-display text-3xl font-extrabold uppercase leading-none text-white sm:text-4xl lg:text-5xl">
                    {t(`fe.i${active}.series`, item.series)}
                  </h3>
                )}
                {item.statement && (
                  <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-titanium/80 sm:text-base">
                    {t(`fe.i${active}.stmt`, item.statement)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const BUSINESS_TYPES = [
  "Commercial Gym", "Premium Fitness Club", "Boutique Gym", "Personal Training Studio",
  "Strength / Performance Gym", "Functional Training Facility", "Private Professional Home Gym",
  "Residential / Condominium Gym", "Hotel / Resort", "Corporate Fitness", "Franchise Location",
];

export const BusinessTypes = ({ index }) => {
  const { t } = useLocale();
  return (
    <section className="section-light relative overflow-hidden" data-testid="business-types-section">
      <div className="container-edge relative py-14 lg:py-20">
        <Reveal>
          <SectionLabel index={index} tone="light">Built Around Your Business</SectionLabel>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 gap-px sm:grid-cols-3 lg:grid-cols-4" data-testid="business-types-grid">
          {BUSINESS_TYPES.map((type, i) => (
            <Reveal key={type} delay={i * 0.03}>
              <div className="flex h-full items-center gap-3 border border-carbon-900/10 bg-white p-4 transition-colors duration-300 hover:border-signal/40 sm:p-5">
                <span className="h-1.5 w-1.5 shrink-0 bg-signal" />
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-carbon-900 sm:text-[11px]">{t(`bt.${i}`, type)}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
